import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeaturedSets } from "@/hooks/useProducts";
import FeaturedProductsDesktop from "./FeaturedProductsDesktop";
import FeaturedProductsMobile from "./FeaturedProductsMobile";

/**
 * FeaturedProducts — Carrusel de sets destacados en la landing page.
 * Selecciona los últimos 8 sets disponibles en la plataforma agregados por la comunidad.
 * Cuenta con arquitectura adaptativa (PC y Móvil):
 * - Desktop: Carrusel continuo suave (marquee) con pausa al hover/click y botones de navegación.
 * - Mobile: Carrusel fluido touch-friendly con pausa al interactuar.
 */
const FeaturedProducts = () => {
  const { t } = useTranslation();
  const { data: featuredSets = [], isLoading } = useFeaturedSets(8);

  return (
    <section data-section="featured" className="py-20 lg:py-24 bg-background overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-2"
            >
              {t("featured.title")}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-muted-foreground text-sm sm:text-base"
            >
              {t("featured.subtitle")}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Button variant="outline" asChild className="group">
              <Link to="/catalogo">
                {t("featured.cta")}
                <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : featuredSets.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No hay sets disponibles en este momento.
          </div>
        ) : (
          <div>
            {/* Visualización Desktop (PC: mouse, controles y scroll horizontal continuo) */}
            <div className="hidden md:block">
              <FeaturedProductsDesktop sets={featuredSets} />
            </div>

            {/* Visualización Móvil (Touch-first, ergonomía táctil y auto-scroll pausado) */}
            <div className="block md:hidden">
              <FeaturedProductsMobile sets={featuredSets} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProducts;
